package org.example.newportfolio.models

import org.example.newportfolio.utils.Res

data class WorkLink(
    val label: String,
    val url: String,
)

enum class Work(
    val workName: String,
    val description: String,
    val link: String,
    val imageRes: String,
    val techStack: List<String> = emptyList(),
    val linkLabel: String = "View Project",
    val category: String = "Mobile Application",
    val highlights: List<String> = emptyList(),
    // Additional store/demo links beyond the primary one (e.g. an app that
    // ships separate Customer and Seller builds on two stores).
    val extraLinks: List<WorkLink> = emptyList(),
    // Product Showcase on the case-study page. Mobile shots render inside
    // portrait phone frames; dashboard shots render inside wide browser-chrome
    // cards. When both are empty the page falls back to the single imageRes
    // figure — used for the flagship full-stack builds (mobile + web).
    val mobileShots: List<String> = emptyList(),
    val dashboardShots: List<String> = emptyList(),
) {
    Ramtex(
        workName = "Ramtex — Distribution Platform",
        description = "Ramtex is a complete B2B distribution platform I built single-handedly, end to " +
                "end — backend, web dashboard, mobile apps, UI/UX design, DevOps and deployment — for a " +
                "tissue and paper-products manufacturer (wet wipes, mega rolls, napkins, sanitary pads). " +
                "One role-aware Flutter app serves three field roles from a single codebase: customers " +
                "browse the catalogue by brand and category and place orders, field-sales reps register " +
                "shops on-site, log visits and take orders door-to-door, and drivers manage their daily " +
                "delivery runs. Behind it, an enterprise web dashboard runs the entire back office — " +
                "products, brands, price lists and stock movements; clients, suppliers and staff; invoices, " +
                "offers, sales returns, receipts and payments — plus a live sales map, sales-team " +
                "performance, and a marketplace & sales analytics suite (revenue vs returns, traffic, " +
                "invoice reports). Designed in Figma, powered by a Laravel REST API, and deployed and " +
                "operated entirely by me. Published on the iOS App Store.",
        link = "https://apps.apple.com/ma/app/ramtexgroup/id6758732248",
        imageRes = Res.Images.RAMTEX_DASH_1,
        techStack = listOf("Laravel", "PHP", "MySQL", "REST API", "Flutter", "Dart", "BLoC", "Dio", "Firebase", "Figma", "DevOps"),
        linkLabel = "App Store",
        category = "Full-Stack Distribution Platform",
        highlights = listOf(
            "Solo end-to-end build: backend API, web dashboard, mobile apps, UI/UX design and deployment",
            "One role-aware Flutter app for three field roles — customers, field-sales reps and drivers",
            "Enterprise back office: inventory, price lists, invoices, offers, returns, receipts and suppliers",
            "Field operations: on-site shop registration, visit capture, live sales map and team performance",
            "Analytics suite — revenue vs returns, marketplace & sales, traffic and invoice reports",
            "Designed in Figma, deployed and operated end-to-end; live on the iOS App Store",
        ),
        mobileShots = listOf(Res.Images.RAMTEX_APP_1, Res.Images.RAMTEX_APP_2, Res.Images.RAMTEX_APP_3),
        dashboardShots = listOf(Res.Images.RAMTEX_DASH_1, Res.Images.RAMTEX_DASH_2, Res.Images.RAMTEX_DASH_3),
    ),
    Purely(
        workName = "Purely - Eco Car Wash",
        description = "A mobile booking platform for an eco-friendly on-demand car-wash service " +
                "operating in Saudi Arabia. Built at Apps Square. Users choose between service tiers " +
                "(Pro Steam Wash, Express Wash, Engine Wash, Interior Detailing), schedule a slot at " +
                "their location via geofenced address picking, and track the order through completion. " +
                "Includes secure OTP-based auth, QR-code service handover, push notifications for order " +
                "updates, in-app subscriptions, and a fully-localized Arabic/English UI. Live on Google " +
                "Play with active production users.",
        link = "https://play.google.com/store/apps/details?id=com.purely.purely",
        imageRes = Res.Images.PURELY,
        techStack = listOf("Flutter", "Dart", "BLoC", "Dio", "GoRouter", "Geolocator", "QR Scanner", "WebView", "Shimmer"),
        linkLabel = "Get on Google Play",
        category = "On-Demand Service App",
        highlights = listOf(
            "Live on Google Play with active production users across Saudi Arabia",
            "Geofenced address picking with scheduled service slots and live order tracking",
            "Secure OTP authentication, QR-code service handover and in-app subscriptions",
            "Fully localized Arabic / English experience",
        )
    ),
    CrepeWaffle(
        workName = "Crepe & Waffle - F&B Ordering",
        description = "Customer-facing ordering app for an Egyptian crepe & waffle restaurant chain, " +
                "plus a responsive companion web ordering experience. Built at Apps Square. The Flutter " +
                "app supports menu browsing across categories, cart management, Google Maps delivery " +
                "address selection, multiple authentication methods (email + Google + Facebook + Apple " +
                "+ biometric), Firebase Cloud Messaging push notifications, QR-code receipt rendering, " +
                "and Lottie-driven loading animations. Live on Google Play.",
        link = "https://play.google.com/store/apps/details?id=com.appssquare.cofferesturant",
        imageRes = Res.Images.CREPE_WAFFLE,
        techStack = listOf("Flutter", "Dart", "BLoC", "Retrofit", "Firebase", "Google Maps", "Google Sign-In", "Apple Sign-In", "Lottie"),
        linkLabel = "Get on Google Play",
        category = "F&B Ordering App",
        highlights = listOf(
            "Live on Google Play for a multi-branch Egyptian restaurant chain",
            "Email, Google, Facebook, Apple and biometric authentication",
            "Google Maps delivery address selection with full cart management",
            "FCM push notifications, QR-code receipts and Lottie-driven animations",
        )
    ),
    Bismar(
        workName = "Bismar - Construction Materials",
        description = "A Kotlin Multiplatform (KMP) marketplace connecting customers with construction " +
                "material sellers, shipped as two dedicated apps — Bismar for customers and Bismar Seller " +
                "for merchants — both live on the App Store and Google Play. Built with Clean Architecture " +
                "and shared business logic across Android and iOS. Features include product browsing, " +
                "seller profiles, order management, and real-time notifications.",
        link = "https://apps.apple.com/pk/app/bismar-%D8%A8%D8%B3%D9%85%D8%A7%D8%B1/id6757424498",
        imageRes = Res.Images.BISMAR,
        techStack = listOf("KMP", "Compose Multiplatform", "Ktor Client", "Clean Architecture", "Koin"),
        linkLabel = "App Store",
        category = "KMP Marketplace App",
        highlights = listOf(
            "Live on the App Store and Google Play with separate Customer and Seller apps",
            "Single Kotlin codebase targeting both Android and iOS",
            "Clean Architecture with fully shared business logic",
            "Product browsing, seller profiles, order management and real-time notifications",
        ),
        extraLinks = listOf(
            WorkLink("Google Play", "https://play.google.com/store/apps/details?id=com.eva.bismar.customer.android&hl=en"),
            WorkLink("Seller • App Store", "https://apps.apple.com/pk/app/bismar-seller-%D8%A8%D8%B3%D9%85%D8%A7%D8%B1-%D8%A7%D9%84%D8%AA%D8%A7%D8%AC%D8%B1/id6755478493"),
            WorkLink("Seller • Google Play", "https://play.google.com/store/apps/details?id=com.eva.bismar.seller.androidApp&hl=en"),
        )
    ),
    EventLens(
        workName = "Event Lens — Photography Marketplace",
        description = "Event Lens (عدسة المناسبات) is a two-sided photography & events marketplace for the " +
                "Saudi market, connecting clients with verified professional photographers across the " +
                "Kingdom. I built both sides of the product: the iOS client app and the web operations " +
                "dashboard. In the app, clients discover photographers by event type — weddings, corporate " +
                "events, product and family sessions — request a booking and follow it through a clear " +
                "multi-step pipeline (request → approval → in-progress → completed) with live status, in-app " +
                "chat and secure booking. The web operations center is where the business runs: an admin " +
                "manages photographers, clients, services, contracts and payments, approves requests, and " +
                "tracks revenue, ratings and approval rates from a live dashboard. Fully bilingual Arabic / " +
                "English (RTL) and live on the App Store.",
        link = "https://apps.apple.com/us/app/batal-al-tasweer/id6772364066",
        imageRes = Res.Images.EVENTLENS_DASH_2,
        techStack = listOf("Flutter", "Dart", "Firebase", "FCM", "Local Notifications", "Secure Storage", "SQLite", "Admin Dashboard", "Localization"),
        linkLabel = "App Store",
        category = "Photography & Events Marketplace",
        highlights = listOf(
            "Built end-to-end: the iOS client app and the web operations dashboard",
            "Multi-step booking pipeline — request, approval, in-progress, completed — with live status",
            "Operations center to manage photographers, clients, services, contracts and payments",
            "In-app chat, secure booking and a fully bilingual Arabic / English (RTL) experience",
        ),
        mobileShots = listOf(Res.Images.EVENTLENS_APP_1, Res.Images.EVENTLENS_APP_2, Res.Images.EVENTLENS_APP_3),
        dashboardShots = listOf(Res.Images.EVENTLENS_DASH_1, Res.Images.EVENTLENS_DASH_2),
    ),
    HotelX(
        workName = "Hotel X - Management System",
        description = "A large-scale Android tablet application for comprehensive hotel management. " +
                "Features include room booking and availability tracking, staff scheduling and task assignment, " +
                "guest management, detailed reporting dashboards, and automated inventory control. " +
                "Improved operational efficiency by 25% through advanced caching and data synchronization.",
        link = "https://youtu.be/SLVSWdPvxeQ",
        imageRes = Res.Images.HOTEL,
        techStack = listOf("Jetpack Compose", "Kotlin", "MVVM", "Retrofit", "Coroutines", "Room", "Hilt"),
        linkLabel = "Watch Demo",
        category = "Tablet Management System",
        highlights = listOf(
            "Improved hotel operational efficiency by 25% via caching and data sync",
            "Room booking with live availability tracking",
            "Staff scheduling, task assignment and guest management",
            "Reporting dashboards with automated inventory control",
        )
    ),
    HoneyMart(
        workName = "Honey Mart App Suite",
        description = "A multi-module Android application suite consisting of three apps: " +
                "User app for browsing and purchasing, Owner app for shop management, " +
                "and Admin app for platform oversight. Built with Jetpack Compose and modern Android architecture " +
                "following single-activity pattern with clean separation of concerns.",
        link = "https://github.com/MohamedElgohary88/Honey-Mart",
        imageRes = Res.Images.HONEY_MART,
        techStack = listOf("Jetpack Compose", "Multi-Module", "MVVM", "Hilt", "Retrofit", "Coroutines"),
        linkLabel = "View on GitHub",
        category = "Multi-Module App Suite",
        highlights = listOf(
            "Three apps from one codebase: User, Owner and Admin",
            "Multi-module, single-activity architecture",
            "Clean separation of concerns with MVVM and Hilt",
            "Modern, fully declarative Jetpack Compose UI",
        )
    ),
    Blog(
        workName = "Full-Stack Blog (KMP)",
        description = "A unified blogging platform built with Kotlin Multiplatform, targeting Android, iOS, and Web " +
                "from a single codebase. Features rich text editing, media uploads, user authentication, " +
                "and responsive design. Reduced development time by 30% through shared business logic.",
        link = "https://github.com/MohamedElgohary88/BlogMultiplatform",
        imageRes = Res.Images.BLOG,
        techStack = listOf("KMP", "Compose Multiplatform", "Ktor", "MongoDB", "Kobweb"),
        linkLabel = "View on GitHub",
        category = "Full-Stack KMP Platform",
        highlights = listOf(
            "Android, iOS and Web shipped from a single Kotlin codebase",
            "Rich text editing with media uploads",
            "User authentication and a fully responsive design",
            "Cut development time ~30% through shared business logic",
        )
    ),
    Teamix(
        workName = "Teamix Project Manager",
        description = "A collaborative project management application that boosted task completion rates by 30% " +
                "for over 500 active users. Features include task boards, team collaboration, " +
                "progress tracking, deadline management, and real-time notifications.",
        link = "https://github.com/MohamedElgohary88/Teamix",
        imageRes = Res.Images.TEAMIX,
        techStack = listOf("Kotlin", "Compose", "MVVM", "Firebase", "Coroutines"),
        linkLabel = "View on GitHub",
        category = "Productivity App",
        highlights = listOf(
            "Boosted task completion rates by 30% for 500+ active users",
            "Task boards with real-time team collaboration",
            "Progress tracking and deadline management",
            "Instant notifications across the whole team",
        )
    ),
    SkyCast(
        workName = "SkyCast Weather App",
        description = "A cross-platform weather application built with Kotlin Multiplatform. " +
                "Provides real-time weather data, 7-day forecasts, location-based alerts, " +
                "and beautiful dynamic UI that adapts to weather conditions.",
        link = "https://github.com/MohamedElgohary88/SkyCast",
        imageRes = Res.Images.SKY_CAST,
        techStack = listOf("KMP", "Ktor Client", "Koin", "Compose", "Coroutines"),
        linkLabel = "View on GitHub",
        category = "Cross-Platform Weather App",
        highlights = listOf(
            "Real-time weather data with 7-day forecasts",
            "Location-based alerts",
            "Dynamic UI that adapts to live weather conditions",
            "Shared KMP logic across every platform",
        )
    ),
    Movie(
        workName = "Movies App",
        description = "An Android application for discovering and tracking movies and TV shows. " +
                "Features include trending movies, search functionality, detailed movie pages, " +
                "watchlist management, and smooth animations with Material Design 3.",
        link = "https://github.com/MohamedElgohary88/MovieApp",
        imageRes = Res.Images.MOVIE,
        techStack = listOf("Kotlin", "Compose", "MVVM", "Retrofit", "Paging 3"),
        linkLabel = "View on GitHub",
        category = "Entertainment App",
        highlights = listOf(
            "Trending movie discovery with rich search",
            "Detailed movie pages and watchlist management",
            "Paging 3 for smooth infinite scrolling",
            "Material Design 3 with fluid animations",
        )
    ),
}